import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@src/components/ui/dialog';

import { ShadcnButton } from '@shadcn-features/core';
import { forwardRef, useImperativeHandle, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

export interface ServiceHyperlinkModalRef<T> {
    open: (data: T) => void;
}

type Props<T> = {
    onContinue: (data: T) => Promise<boolean>;
};

const InnerServiceHyperlinkModal = <T,>(
    { onContinue }: Props<T>,
    ref: Ref<ServiceHyperlinkModalRef<T>>,
) => {
    const { t } = useTranslation();
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        open(nextData) {
            setData(nextData);
        },
    }));

    const close = () => {
        if (!isLoading) setData(null);
    };

    return (
        <Dialog
            open={data !== null}
            onOpenChange={(open) => {
                if (!open) close();
            }}
        >
            <DialogContent className='max-w-[480px] rounded-xl p-8 shadow-[0_10px_15px_-3px_rgb(0_0_0_/_0.1),0_4px_6px_-4px_rgb(0_0_0_/_0.1)]'>
                <div className='flex flex-col gap-4'>
                    <DialogTitle>{t('translation:unsavedChanges')}</DialogTitle>
                    <DialogDescription className='m-0 text-sm leading-[21px] tracking-[0.07px] text-(--color-card-subtitle)'>
                        {t('translation:unsavedChangesMessage')}
                    </DialogDescription>
                    <div className='flex flex-wrap justify-end gap-2'>
                        <ShadcnButton
                            className='shadow-[0_1px_3px_0_rgb(0_0_0_/_0.1),0_1px_2px_-1px_rgb(0_0_0_/_0.1)]'
                            disabled={isLoading}
                            size='md'
                            variant='outline'
                            onClick={close}
                        >
                            {t('translation:cancel')}
                        </ShadcnButton>
                        <ShadcnButton
                            loading={isLoading}
                            size='md'
                            variant='brandGreen'
                            onClick={async () => {
                                if (data === null) return;

                                setIsLoading(true);
                                try {
                                    const shouldClose = await onContinue(data);
                                    if (shouldClose) setData(null);
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                        >
                            {t('translation:continueWithoutSaving')}
                        </ShadcnButton>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const ServiceHyperlinkModal = forwardRef(InnerServiceHyperlinkModal) as <
    T,
>(
    props: Props<T> & { ref?: Ref<ServiceHyperlinkModalRef<T>> },
) => ReturnType<typeof InnerServiceHyperlinkModal>;
